import { Marker } from 'mapbox-gl'
import { bbox } from '@turf/turf'
import { convertLinkToBackEnd } from '@utils/url'
import { get } from 'lodash'
import { getMap } from './store/store-map'
import { getRandomColor } from '@utils/color'
import { getUUIDv4 } from '@utils/helper'

function loadImage(map, link) {
  return new Promise(function(resolve, reject) {
    map.loadImage(link, function(error, image) {
      if (error) reject(error)
      else resolve(image)
    })
  })
}

function drawUrlToImageData(url, { size = 32, forceSize = false } = {}) {
  return new Promise(function(resolve, reject) {
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = function() {
      const width = forceSize ? size : img.naturalWidth || size
      const height = forceSize ? size : img.naturalHeight || size
      const canvas = document.createElement('canvas')
      canvas.width = width
      canvas.height = height
      const ctx = canvas.getContext('2d')
      ctx.clearRect(0, 0, width, height)
      // Keep aspect ratio inside the target box when forcing size.
      if (forceSize) {
        const srcW = img.naturalWidth || size
        const srcH = img.naturalHeight || size
        const scale = Math.min(width / srcW, height / srcH)
        const drawW = srcW * scale
        const drawH = srcH * scale
        const offsetX = (width - drawW) / 2
        const offsetY = (height - drawH) / 2
        ctx.drawImage(img, offsetX, offsetY, drawW, drawH)
      } else {
        ctx.drawImage(img, 0, 0, width, height)
      }
      resolve(ctx.getImageData(0, 0, width, height))
    }
    img.onerror = reject
    img.src = url
  })
}

function isSvgBlob(blob, link = '') {
  const type = (blob && blob.type) || ''
  if (type.includes('svg')) return true
  return /\.svg(\?|$)/i.test(link || '')
}

async function rasterizeRemoteImage(link, options = {}) {
  const response = await fetch(link, { credentials: 'include' })
  if (!response.ok) {
    throw new Error(`Could not fetch image: ${response.status}`)
  }
  const blob = await response.blob()
  const objectUrl = URL.createObjectURL(blob)
  try {
    const rasterOptions = isSvgBlob(blob, link)
      ? { size: 32, forceSize: true, ...options }
      : { size: 32, ...options }
    return await drawUrlToImageData(objectUrl, rasterOptions)
  } finally {
    URL.revokeObjectURL(objectUrl)
  }
}

async function loadMapboxCompatibleImage(map, link, options) {
  try {
    return await loadImage(map, link)
  } catch (error) {
    // Mapbox cannot decode SVG; rasterize via canvas to ImageData (default 32x32).
    return await rasterizeRemoteImage(link, options)
  }
}

export async function createMapboxImageUrl(mapId, id, link, option) {
  let map = getMap(mapId)
  if (!map.hasImage(id)) {
    map.addImage(id, await loadMapboxCompatibleImage(map, link), option)
  }

  return id
}
export async function createMapboxImage(mapId, id, link) {
  let map = getMap(mapId)
  if (!map.hasImage(id)) {
    map.addImage(id, await createHTMLImageElement(link))
  }

  return id
}
function createHTMLImageElement(link) {
  return new Promise(function(resolve, reject) {
    const img = document.createElement('img')
    img.src = link
    img.width = 24
    img.height = 24

    img.onload = resolve(img)
    img.onerror = reject
  })
}
export function createMarkerNode({
  color,
  size = 12,
  draggable = true,
  borderSize = 2,
  content,
} = {}) {
  const element = document.createElement('div')
  element.style.width = size + 'px'
  element.style.height = size + 'px'
  element.style.borderRadius = '50%'
  element.style.background = '#fff'
  element.style.boxSizing = 'border-box'
  element.style.border = `${borderSize}px solid ${color}`
  element.style.cursor = draggable ? 'move' : null
  if (content) {
    element.innerHTML = `<div style="position: absolute;
                      left: 50%;
                      top: 54%;
                      -webkit-transform: translate(-50%, -50%);
                      transform: translate(-50%, -50%);
                      border-radius: 50%;">
                        <p style="margin:0;">${content}</p>
                      </div>`
  }
  let marker = new Marker({
    element,
    draggable,
  })
  return {
    marker,
    element,
  }
}

export const fitBounds = (mapId, value, { zoom = 15 } = {}) => {
  let map = getMap(mapId)
  if (!map) return
  if (!value || !map) return
  let padding = {
    top: 50,
    bottom: 50,
    left: 50,
    right: 50,
  }

  if (['Point'].includes(value.type)) {
    map.easeTo({
      center: value.coordinates,
      zoom,
      duration: 0,
      padding,
    })
    return
  }
  let count = { left_count: 1, right_count: 0 }
  padding.left = count.left_count ? 450 : padding.left
  padding.right = count.right_count ? 450 : padding.right

  let bboxFil = undefined
  if (value.length == 4) {
    bboxFil = value
  } else {
    bboxFil = getBBox(value)
  }

  if (!bboxFil) return

  map.fitBounds(bboxFil, {
    padding,
    duration: 0,
  })
}

function getBBox(feature) {
  if (!feature.type) return
  let bboxFil = undefined
  if (['Feature', 'FeatureCollection'].includes(feature.type)) {
    bboxFil = bbox(feature)
  } else {
    bboxFil = bbox({
      type: 'Feature',
      properties: {},
      geometry: feature,
    })
  }
  return bboxFil
}
export function createIconForLayerObject(layer) {
  let icon_id = null

  let is_style_custom_edit = get(layer, 'meta.style.custom_edit', false)

  let style = is_style_custom_edit ? get(layer, 'meta.style') : {}
  let icon_link = get(style, 'icon')
  if (icon_link) {
    icon_id = `icon_${getUUIDv4()}`
  }
  return { icon_id, icon_link }
}
export async function addIconForLayerObject(map, icon_id, layer) {
  if (!icon_id || !map || !layer) return
  let is_style_custom_edit = get(layer, 'meta.style.custom_edit', false)

  let style = is_style_custom_edit ? get(layer, 'meta.style') : {}
  let icon_link = get(style, 'icon')
  if (icon_id) {
    if (!map.hasImage(icon_id)) {
      map.addImage(
        icon_id,
        await loadMapImage(map, convertLinkToBackEnd(icon_link))
      )
    }
  }
  return icon_id
}
export function createLayerMapFromLayerObject(x, { default_color, icon } = {}) {
  if (!x || !x.shape_type) return
  let result = {
    name: x.name,
    source: '',
    layout: {},
    paint: {},
  }
  let is_style_custom_edit = get(x, 'meta.style.custom_edit', false)
  let style = is_style_custom_edit ? get(x, 'meta.style') : {}
  if (x.shape_type == 'POINT') {
    if (icon) {
      result.type = 'symbol'
      result.layout = {
        'icon-size': +get(style, 'size', 5),
        'icon-image': icon,
      }
      result.paint = {
        'icon-opacity': +get(style, 'opacity', 1),
      }
    } else {
      result.type = 'circle'
      result.paint = {
        'circle-radius': +get(style, 'size', 5),
        'circle-color': get(style, 'color', default_color || getRandomColor()),
        // 'circle-stroke-color': '#000000',
        // 'circle-stroke-width': 1,
        'circle-opacity': +get(style, 'opacity', 1),
      }
    }
  } else if (x.shape_type == 'LINE') {
    result.type = 'line'
    result.paint = {
      'line-color': get(style, 'color', default_color || getRandomColor()),
      'line-width': +get(style, 'size', 5),
      'line-opacity': get(style, 'opacity', 1),
    }
  } else if (x.shape_type == 'POLYGON') {
    result.type = 'fill'
    result.paint = {
      'fill-color': get(style, 'color', default_color || getRandomColor()),
      'fill-opacity': get(style, 'opacity', 1),
      'fill-outline-color': '#000000',
    }
  } else {
    throw new Error('not support ' + x.shape_type)
  }
  return result
}
export function loadMapImage(map, link) {
  return new Promise(function(resolve, reject) {
    map.loadImage(link, function(error, image) {
      if (error) reject(error)
      resolve(image)
    })
  })
}

export function getScaleFromMap(map, options) {
  const maxWidth = (options && options.maxWidth) || 100

  const y = map._container.clientHeight / 2
  const left = map.unproject([0, y])
  const right = map.unproject([maxWidth, y])
  const maxMeters = left.distanceTo(right)

  return getScale(maxMeters, map._getUIString('ScaleControl.Meters'))
}

function getScale(maxDistance, unit) {
  const distance = getRoundNum(maxDistance)
  return {
    distance,
    unit,
  }
}

function getDecimalRoundNum(d) {
  const multiplier = Math.pow(10, Math.ceil(-Math.log(d) / Math.LN10))
  return Math.round(d * multiplier) / multiplier
}

function getRoundNum(num) {
  const pow10 = Math.pow(10, `${Math.floor(num)}`.length - 1)
  let d = num / pow10

  d =
    d >= 10
      ? 10
      : d >= 5
      ? 5
      : d >= 3
      ? 3
      : d >= 2
      ? 2
      : d >= 1
      ? 1
      : getDecimalRoundNum(d)

  return pow10 * d
}
